/**
 * THE ONE navigation config (PROJECT-KNOWLEDGE.md §Navigation, SPEC-APP.md §2.3).
 *
 * Groups are in the document's order. Every item's `permission` is COPIED from the route
 * table entry of its path — never re-decided here. Dictionary nouns are carried as
 * `labelKey` and resolved through `useLabels`; everything else is a literal.
 */
import { useMemo } from "react";
import {
  Activity,
  BarChart3,
  Database,
  FlaskConical,
  Home,
  Layers,
  ListChecks,
  MapPinned,
  Package,
  PackageX,
  Route as RouteIcon,
  Rss,
  Search,
  ScrollText,
  Server,
  Ship,
  TriangleAlert,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useBootstrap } from "../api/queries/useBootstrap";
import { usePlanningScope } from "../api/queries/usePlanningScope";
import type { LabelKey } from "../domain/labels";
import { hasPermission, type PermissionRule } from "../domain/permissions";
import { routeEntry } from "../routes/routeTable";
import { useLabels } from "./useLabels";
import { useModuleNavigation } from "./useEnabledModules";

/**
 * The five Analytics children are built (SPEC-APP.md §12.1), so they show. Each child's
 * `permission` is its route's own (`analytics:read`), copied by `item()`; the parent is gated by
 * the first child's rule and is absent when no child is visible. A platform without its own
 * variant of a screen falls back to desktop (`resolveVariant`), so no platform hides an entry.
 */
export const analyticsAvailable = true;

export type NavGroupId =
  "home" | "plan" | "reports" | "data" | "workspaces" | "demand" | "feeds" | "admin";

export interface NavItem {
  path: string;
  /** A dictionary noun, resolved through `useLabels`. */
  labelKey?: LabelKey;
  /** A literal, for labels the dictionary does not hold. */
  label?: string;
  icon: LucideIcon;
  permission: PermissionRule;
  /** A parent expands and does not navigate on desktop. */
  children?: NavItem[];
  /** Set on an item spliced in from a customer module. */
  moduleKey?: string;
  /** Shown only when the tenant has more than one planning scope (PS-D12). */
  multiScopeOnly?: boolean;
}

export interface NavGroup {
  id: NavGroupId;
  /** Omitted on the Home group, which is a single unlabelled item. */
  label?: string;
  items: NavItem[];
}

/** Copies the rule the route table already decided for this path. */
const rule = (path: string): PermissionRule => routeEntry(path).permission;

const item = (
  path: string,
  icon: LucideIcon,
  naming: { labelKey: LabelKey } | { label: string },
): NavItem => ({ path, icon, permission: rule(path), ...naming });

export const navConfig: NavGroup[] = [
  {
    id: "home",
    items: [item("/", Home, { label: "Home" })],
  },
  {
    id: "plan",
    label: "Plan",
    items: [
      item("/runs", Activity, { labelKey: "planningRuns" }),
      item("/planning/issues", PackageX, { label: "Planning issues" }),
      item("/experiments", FlaskConical, { labelKey: "experiments" }),
      {
        path: "/analytics",
        label: "Analytics",
        icon: BarChart3,
        permission: rule("/analytics/spend"),
        children: analyticsAvailable
          ? [
              item("/analytics/spend", BarChart3, { label: "Spend" }),
              item("/analytics/savings", BarChart3, { label: "Savings" }),
              item("/analytics/findings", Activity, { label: "Plan health" }),
              item("/analytics/explain", ScrollText, { label: "Explain" }),
              item("/analytics/compare", Layers, { label: "Compare runs" }),
            ]
          : [],
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    items: [
      /* Report tables share the hub; the visual network map keeps its own surface. */
      item("/reports", BarChart3, { label: "Reports" }),
      item("/reports/map", MapPinned, { label: "Network map" }),
    ],
  },
  {
    id: "data",
    label: "Data",
    items: [
      item("/data", Database, { label: "Overview" }),
      item("/deliveries", Rss, { label: "Data feed log" }),
      item("/data/issues", TriangleAlert, { label: "Data feed issues" }),
    ],
  },
  {
    id: "workspaces",
    label: "Workspaces",
    items: [
      item("/site-workspace", Search, { labelKey: "siteWorkspace" }),
      item("/lane-workspace", RouteIcon, { labelKey: "laneWorkspace" }),
    ],
  },
  {
    id: "admin",
    label: "Admin",
    items: [
      item("/audit", ScrollText, { label: "Application logs" }),
      {
        ...item("/admin/planning-scopes", Layers, { label: "Planning scopes" }),
        multiScopeOnly: true,
      },
      item("/admin/members", Users, { label: "Members" }),
      item("/admin/queue", Server, { label: "Queue" }),
    ],
  },
];

/** Resolves an item's visible text against the merged dictionary. */
export function navLabel(navItem: NavItem, labels: Record<string, string>): string {
  return navItem.labelKey ? (labels[navItem.labelKey] ?? navItem.path) : (navItem.label ?? "");
}

/**
 * The config filtered by the user's held permissions, with every enabled module's
 * server-sent navigation entries spliced into the group they name, after the core items.
 */
export function useNavGroups(): NavGroup[] {
  const { data } = useBootstrap();
  const moduleNav = useModuleNavigation();
  const held = data?.user.permissions;
  const { multiScope } = usePlanningScope();

  return useMemo(() => {
    const permissions: readonly string[] = held ?? [];
    const groups: NavGroup[] = [];

    for (const group of navConfig) {
      const items: NavItem[] = [];
      for (const navItem of group.items) {
        if (!hasPermission(permissions, navItem.permission)) continue;
        if (navItem.multiScopeOnly && !multiScope) continue;
        if (navItem.children) {
          const children = navItem.children.filter((c) => hasPermission(permissions, c.permission));
          // A parent with no visible children is absent, not an empty expander.
          if (children.length === 0) continue;
          items.push({ ...navItem, children });
          continue;
        }
        items.push(navItem);
      }

      for (const entry of moduleNav) {
        if (entry.group !== group.id) continue;
        if (!hasPermission(permissions, entry.permission)) continue;
        items.push({
          path: entry.path,
          label: entry.label,
          icon: entry.icon,
          permission: entry.permission,
          moduleKey: entry.path,
        });
      }

      if (items.length > 0) groups.push({ ...group, items });
    }

    return groups;
  }, [held, moduleNav, multiScope]);
}

/** The flat list of visible leaf items, for the phone sheet and title lookups. */
export function useNavLeaves(): { group: NavGroup; item: NavItem }[] {
  const groups = useNavGroups();
  return useMemo(
    () =>
      groups.flatMap((group) =>
        group.items.flatMap((navItem) =>
          navItem.children
            ? navItem.children.map((child) => ({ group, item: child }))
            : [{ group, item: navItem }],
        ),
      ),
    [groups],
  );
}

/** A hook-free label resolver for consumers that already hold the dictionary. */
export function useNavLabel(): (navItem: NavItem) => string {
  const labels = useLabels() as unknown as Record<string, string>;
  return (navItem: NavItem) => navLabel(navItem, labels);
}
